import { useQuery } from '@apollo/client';
import * as icons from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DateRange,
  DateRangePicker,
  PickersShortcutsItem,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { MetadataFilter, MetadataFiltersDialog } from 'components/common/filters/metadata-filter';
import { MetadataItemExpandable } from 'components/common/metadata/metadata-item';
import { MinimumLeadCreatedDateByCompanyQuery } from 'components/views/_shared-queries/company-settings.graphql';
import { FormSwitch } from 'components/views/forms/form-checkbox';
import { endOfWeek, startOfWeek, subDays } from 'date-fns';
import { useQueryParams } from 'hooks/use-query-params';
import moment from 'moment-timezone';
import { useDemoMode } from 'providers/demo-mode';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router';
import {
  Conversation_externalLead_phoneInfo,
  ConversationVariables,
} from 'types/apollo/Conversation';
import {
  Leads,
  Leads_externalLeads_leads,
  Leads_externalLeads_statusCounts,
  LeadsVariables,
} from 'types/apollo/Leads';
import { MinimumLeadCreatedDateByCompany } from 'types/apollo/MinimumLeadCreatedDateByCompany';
import { useDebouncedEffect } from 'utils/debounce-effect.hook';
import { FormTextField } from '../forms/form-text-field';
import { getTotalCountByFilters } from './conversation-helpers';
import { LeadsQuery } from './conversation.graphql';
import { ConversationItem } from './ConversationItem';
import { NOT_SET, statusLabelOverrides } from './lead-status-chip';
import { addPlusOne, phoneRegExp } from './phone-number';

// ============================================================================
// Constants
// ============================================================================

const beginningOfAktify = new Date(2020, 2, 9);
const today = new Date();

const shortcutsItems: PickersShortcutsItem<DateRange<Date>>[] = [
  {
    label: 'Today',
    getValue: () => {
      return [today, today];
    },
  },
  {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = subDays(today, 1);
      return [yesterday, yesterday];
    },
  },
  {
    label: 'This Week',
    getValue: () => {
      return [startOfWeek(today, { weekStartsOn: 1 }), today];
    },
  },
  {
    label: 'Last Week',
    getValue: () => {
      const prevWeek = subDays(today, 7);
      return [startOfWeek(prevWeek, { weekStartsOn: 1 }), endOfWeek(prevWeek, { weekStartsOn: 1 })];
    },
  },
];

// ============================================================================
// Styled Components
// ============================================================================

const StyledConversationList = styled('div')(() => ({
  width: '300px',
  maxWidth: '300px',
  minWidth: '250px',
  overflowY: 'auto',
  flex: '1 1 0%',
  display: 'flex',
  flexDirection: 'column',
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: '1px solid #ccc',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 8,
  justifyContent: 'space-between',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '120px',
}));

// ============================================================================
// Helper Components
// ============================================================================

const MultiSelect: FC<{
  selectedValues: string[];
  setSelectedValues: (names: string[]) => void;
  label: string;
  disabled: boolean;
  children: React.ReactNode;
}> = ({ selectedValues, setSelectedValues, label, disabled, children }) => {
  return (
    <FormControl sx={{ display: 'flex', flex: '1 1 124px', minWidth: '124px' }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        disabled={disabled}
        value={selectedValues}
        onChange={(e) => setSelectedValues(e.target.value as string[])}
        renderValue={(selected) => selected.map((s) => statusLabelOverrides[s] ?? s).join(', ')}
        MenuProps={{
          slotProps: {
            paper: {
              style: {
                minWidth: '294px',
              },
            },
          },
        }}
      >
        {children}
      </Select>
    </FormControl>
  );
};

const statusMenuItem = (
  status: string,
  userFilters: string[] | null,
  count: string,
  setHoveringOver: (status: string | null) => void,
  hovering: boolean,
  setFilters: (status: string[]) => void,
) => (
  <MenuItem
    key={status}
    value={status}
    onMouseEnter={() => setHoveringOver(status)}
    onMouseLeave={() => setHoveringOver(null)}
  >
    <ListItemIcon>
      <ListItemIcon>
        <Checkbox checked={userFilters == null || userFilters.includes(status)} />
      </ListItemIcon>
    </ListItemIcon>
    <ListItemText
      sx={{ flexGrow: 'unset', marginRight: 1 }}
      primary={statusLabelOverrides[status] ?? status}
    />
    <ListItemText secondary={`(${count})`} />
    {hovering && (
      <ListItemSecondaryAction>
        <Button
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            setFilters([status]);
          }}
        >
          Only
        </Button>
      </ListItemSecondaryAction>
    )}
  </MenuItem>
);

const PhoneHelperText: FC = () => {
  return (
    <Tooltip
      title={
        <Typography fontSize="16px" textAlign="center">
          Omitting &apos;+1&apos; defaults to a US number.
        </Typography>
      }
    >
      <Stack direction="row" fontSize="12px">
        Format: +15551234567
        <icons.InfoOutlined sx={{ marginLeft: 1, color: 'text.secondary', fontSize: '18px' }} />
      </Stack>
    </Tooltip>
  );
};

export const PhoneInfoMetadata: FC<{ phoneInfo: Conversation_externalLead_phoneInfo }> = ({
  phoneInfo,
}) => {
  return (
    <MetadataItemExpandable
      text="Phone info"
      initiallyOpen
      values={[
        ['Carrier', phoneInfo.carrier],
        ['Line type', phoneInfo.lineType ?? 'Unknown'],
        [
          'Last checked',
          <Tooltip
            key={phoneInfo.lastCheck}
            title={moment(phoneInfo.lastCheck).format('MM/DD/YYYY hh:mm A')}
          >
            <span>{moment(phoneInfo.lastCheck).fromNow()}</span>
          </Tooltip>,
        ],
      ]}
    />
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const ConversationList: FC<{
  fetchConversation: (variables: ConversationVariables) => Promise<any>;
  onSelect: (phone: string, campaignId?: string) => void;
  selectedPhone?: string;
  loadingConversation: boolean;
  children?: React.ReactNode;
  hideFilters?: boolean;
  companyId?: string;
}> = ({
  onSelect,
  selectedPhone,
  fetchConversation,
  loadingConversation,
  children,
  hideFilters,
  companyId,
}) => {
  const { isInDemoMode } = useDemoMode();
  const queryParams = useQueryParams();
  const initialRender = useRef(true);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [leads, setLeads] = useState<Leads_externalLeads_leads[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const { campaignId } = useParams<{ campaignId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignIdFromUrl = searchParams.get('campaignId');
  const isCompanyWideView = !campaignId;
  const [statusCounts, setStatusCounts] = useState<Leads_externalLeads_statusCounts[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [dateRangeHasError, setDateRangeHasError] = useState(false);
  const [minimumLeadCreatedDate, setMinimumLeadCreatedDate] = useState<Date>(beginningOfAktify);
  const form = useForm<{ phone: string; hasRepliesOnly: boolean }>({
    defaultValues: { phone: selectedPhone ?? '', hasRepliesOnly: false },
    mode: 'all',
  });

  const [postedDateRange, setPostedDateRange] = useState<DateRange<Date>>([new Date(), new Date()]);

  const handleDateChange = (date: DateRange<Date> | null) => {
    if (!date || !date[0] || !date[1]) return;
    setPostedDateRange(date);
  };

  const [systemFilters, setSystemFilters] = useState<string[]>([]);
  const [userFilters, setUserFilters] = useState<string[] | null>(null);
  const [hoverOver, setHoveringOver] = useState<string | null>(null);
  const [metadataFilter, setMetadataFilter] = useState<MetadataFilter | null>(null);
  const [metadataFilterQuery, setMetadataFilterQuery] = useState<string>('');
  const phoneFieldNotEmpty = form.watch('phone').length > 0;
  const isAllSelected =
    userFilters == null || userFilters.length == 0 || userFilters.length === statusCounts.length;

  const setFilters = (filters: string[]) => {
    if (filters.includes('all')) {
      setUserFilters(null);
    } else {
      setUserFilters(filters);
    }
  };

  const { refetch, fetchMore } = useQuery<Leads, LeadsVariables>(LeadsQuery, {
    skip: true,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const newLeads = data.externalLeads.leads;
      setLeads(newLeads);
      setCursor(data.externalLeads.cursor);
      setTotalCount(data.externalLeads.totalCount);
      setStatusCounts(data.externalLeads.statusCounts);
      const newFilters = data.externalLeads.statusCounts.map((x) => x.status ?? NOT_SET);
      if (userFilters == null) {
        setSystemFilters(newFilters);
      }
      const totalCountByFilters = getTotalCountByFilters(
        newFilters,
        data.externalLeads.statusCounts,
      );
      setHasMore(newLeads.length < totalCountByFilters);
    },
  });

  const fetchLeads = () => {
    const phoneFilter = form.getValues('phone');
    const phoneWithPlus = addPlusOne(phoneFilter);
    const outreachTemplateId = queryParams.get('oti');
    setLoading(true);

    refetch({
      ...(campaignId && { campaignId }),
      ...(companyId && { companyId }),
      take: 100,
      startDate: moment(postedDateRange[0]).startOf('day').toDate(),
      endDate: moment(postedDateRange[1]).endOf('day').toDate(),
      phone: phoneWithPlus,
      status: userFilters != null && userFilters.length > 0 ? userFilters : null,
      before: null,
      outreachTemplateId: outreachTemplateId ? parseInt(outreachTemplateId) : null,
      hasRepliesOnly: form.getValues('hasRepliesOnly'),
      includeCampaignName: isCompanyWideView,
      metadataFilter,
    }).finally(() => setLoading(false));
  };

  useQuery<MinimumLeadCreatedDateByCompany>(MinimumLeadCreatedDateByCompanyQuery, {
    onCompleted: (data) => {
      if (data.getMinimumLeadCreatedDateByCompany) {
        setMinimumLeadCreatedDate(new Date(data.getMinimumLeadCreatedDateByCompany));
      }
    },
  });

  // fetch leads as soon as page loads
  useEffect(fetchLeads, []);

  useEffect(() => {
    form.trigger('phone');
  }, []);

  useDebouncedEffect(
    () => {
      if (initialRender.current) {
        initialRender.current = false;
        return;
      }
      if (dateRangeHasError) return;
      if (form.getFieldState('phone').error) return;

      fetchLeads();
    },
    [
      postedDateRange,
      /* metadataFilter, */ userFilters,
      form.watch('phone'),
      form.watch('hasRepliesOnly'),
    ],
    750,
  );

  const handleDialogClose = () => {
    if (dateRangeHasError) return;
    if (form.getFieldState('phone').error) return;

    fetchLeads();
  };

  useEffect(() => {
    const phoneFilter = form.getValues('phone');
    const phoneWithPlus = addPlusOne(phoneFilter);
    const firstPhone = leads.length > 0 ? leads[0].phone : null;

    const persistedSelectionExists = campaignId
      ? selectedPhone && leads.some((lead) => lead.phone === selectedPhone)
      : selectedPhone &&
        campaignIdFromUrl &&
        leads.some(
          (lead) => lead.phone === selectedPhone && lead.campaignShortId === campaignIdFromUrl,
        );

    if (phoneFilter && firstPhone && phoneWithPlus == firstPhone && !persistedSelectionExists) {
      const firstCampaignId = leads[0].campaignShortId;
      onSelect(firstPhone, !campaignId ? firstCampaignId : undefined);

      if (campaignId) {
        fetchConversation({
          campaignId,
          phone: firstPhone,
          includeCampaignName: isCompanyWideView,
        });
      } else {
        fetchConversation({
          ...(companyId && { companyId }),
          ...(firstCampaignId && { campaignId: firstCampaignId }),
          phone: firstPhone,
          includeCampaignName: isCompanyWideView,
        });
      }
    } else if (leads && firstPhone) {
      const previousSelectedPhone = selectedPhone;
      const idx = leads.findIndex((x) => x.phone == previousSelectedPhone);

      if (firstPhone != previousSelectedPhone && idx == -1) {
        const firstCampaignId = leads[0].campaignShortId;
        onSelect(firstPhone, !campaignId ? firstCampaignId : undefined);

        if (campaignId) {
          fetchConversation({
            campaignId,
            phone: firstPhone,
            includeCampaignName: isCompanyWideView,
          });
        } else {
          fetchConversation({
            ...(companyId && { companyId }),
            ...(firstCampaignId && { campaignId: firstCampaignId }),
            phone: firstPhone,
            includeCampaignName: isCompanyWideView,
          });
        }
      }
    }
  }, [leads]);

  useEffect(() => {
    setCursor(null);
  }, [userFilters]);

  const handleConversationClick = async (phone: string, selectedCampaignId?: string) => {
    onSelect(phone, !campaignId ? selectedCampaignId : undefined);
    await fetchConversation({
      ...(selectedCampaignId && { campaignId: selectedCampaignId }),
      ...(!selectedCampaignId && campaignId && { campaignId }),
      ...(companyId && { companyId }),
      phone,
      includeCampaignName: isCompanyWideView,
    });
  };

  const loadMore = async () => {
    if (hasMore && !loading && cursor) {
      setLoading(true);
      const d = await fetchMore({
        variables: {
          before: cursor,
          includeCampaignName: isCompanyWideView,
        },
      });

      const incomingLeads: Leads = d.data as Leads;

      if (incomingLeads.externalLeads.leads) {
        const newLeads = [...leads, ...incomingLeads.externalLeads.leads];
        setLeads(newLeads);
        setCursor(incomingLeads.externalLeads.cursor);
        const totalCountByFilters = getTotalCountByFilters(
          userFilters ?? systemFilters,
          d.data.externalLeads.statusCounts,
        );
        setHasMore(newLeads.length < totalCountByFilters);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    });

    if (loading || !hasMore) {
      return;
    }

    const placeholder = document.getElementById('infinite-scroll-placeholder');
    if (placeholder) {
      observer.observe(placeholder);
    }

    return () => {
      observer.disconnect();
    };
  }, [loading, hasMore]);

  const handleClearPhoneButton = () => {
    setUserFilters(null);
    form.setValue('phone', '');
    form.clearErrors('phone');

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('phone');
    if (!campaignId) {
      newParams.delete('campaignId');
    }
    setSearchParams(newParams);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
      }}
    >
      <FilterContainer>
        <Typography fontWeight="bold">Filters</Typography>

        {hideFilters ? null : (
          <>
            <Box>
              <DateRangePicker
                disabled={phoneFieldNotEmpty}
                sx={{ '& input': { width: '200px' } }}
                slots={{ field: SingleInputDateRangeField }}
                format="MM/dd/yyyy"
                slotProps={{
                  textField: {
                    label: 'Posted',
                  },
                  shortcuts: { items: shortcutsItems },
                  actionBar: { actions: [] },
                }}
                calendars={1}
                onError={(error) => {
                  setDateRangeHasError(error.some((x) => x != null));
                }}
                shouldDisableDate={(day) => {
                  return day.getTime() < minimumLeadCreatedDate.getTime();
                }}
                disableFuture
                value={postedDateRange}
                onChange={handleDateChange}
              />
            </Box>

            <MultiSelect
              label="Filter by status"
              disabled={phoneFieldNotEmpty}
              selectedValues={userFilters ?? systemFilters}
              setSelectedValues={setFilters}
            >
              {statusCounts.map(({ status, count }) => {
                return statusMenuItem(
                  status ?? NOT_SET,
                  userFilters,
                  count,
                  setHoveringOver,
                  hoverOver == (status ?? NOT_SET),
                  setUserFilters,
                );
              })}
              <Divider />
              <MenuItem key="all" value="all">
                <ListItemIcon>
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={
                      userFilters != null &&
                      userFilters.length > 0 &&
                      userFilters.length < systemFilters.length
                    }
                  />
                </ListItemIcon>
                <ListItemText sx={{ flexGrow: 'unset', marginRight: 1 }} primary="Select all" />
                <ListItemText secondary={`(${totalCount})`} />
              </MenuItem>
            </MultiSelect>
          </>
        )}

        {!isInDemoMode && (
          <FormTextField
            form={form}
            label="Phone number"
            helperTextComponent="div"
            sx={{ width: '184px', minWidth: '184px', height: '64px' }}
            name="phone"
            optional
            customValidate={phoneRegExp}
            customValidateMessage="Format: +15554329876"
            helperText={<PhoneHelperText />}
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleClearPhoneButton}>
                  <icons.Close />
                </IconButton>
              ),
            }}
          />
        )}

        {hideFilters ? null : (
          <FormSwitch form={form} name="hasRepliesOnly" label="Has replies only" />
        )}

        {!hideFilters && (
          <MetadataFiltersDialog
            value={metadataFilter}
            onChange={setMetadataFilter}
            initialQuery={metadataFilterQuery}
            onQueryChange={setMetadataFilterQuery}
            onDialogClose={handleDialogClose}
            disabled={phoneFieldNotEmpty}
            disabledMessage={`Phone filter is specified so these filters ${
              metadataFilter ? 'will not' : 'cannot'
            } be used`}
          />
        )}

        <Grid flexGrow={1}>
          <Typography whiteSpace="nowrap">
            Showing {leads.length} of {totalCount}
          </Typography>
        </Grid>
      </FilterContainer>

      <Box sx={{ flexDirection: 'row', display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <StyledConversationList>
          {loading && !loadingConversation && <LinearProgress sx={{ height: '6px' }} />}
          {leads.map((conversation) => (
            <ConversationItem
              key={`${conversation.phone}-${conversation.campaignShortId}`}
              onClick={() =>
                handleConversationClick(conversation.phone, conversation.campaignShortId)
              }
              phoneNumber={conversation.phone}
              status={conversation.status ?? conversation.undeliverableReason}
              lastMessage={conversation.mostRecentMessage?.body}
              firstName={conversation.firstName}
              lastName={conversation.lastName}
              time={conversation.mostRecentMessage?.created ?? conversation.createdAt}
              isSelected={
                conversation.phone === selectedPhone &&
                (campaignId ? true : conversation.campaignShortId === campaignIdFromUrl)
              }
              campaignShortId={conversation.campaignShortId}
              campaignName={conversation.campaignName}
              showCampaign={isCompanyWideView}
            />
          ))}
          {loading && leads.length >= 5 && <LinearProgress />}
          {!loading && !hasMore && (
            <Typography textAlign="center" sx={{ padding: '8px', wordBreak: 'break-word' }}>
              No more conversations to load
            </Typography>
          )}
          {!loading && hasMore && (
            <Button onClick={() => loadMore()} variant="contained" fullWidth>
              Load more
            </Button>
          )}
          <div id="infinite-scroll-placeholder" style={{ height: '5px' }}></div>
        </StyledConversationList>
        {children}
      </Box>
    </Box>
  );
};
